import { useEffect, useRef, useState } from "react";
import cn from "classnames";
import { Button, BUTTON_VARIANT } from "components/UI/Button";
import { ArrowLeftRotate, ArrowRightRotate } from "components/UI/Icons";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useOnClickOutside } from "hooks/useOnClickOutside.hook";
import { useTranslation } from "localization/localization";
import { importGroups, setCustomPallets, setUnitSystem } from "store";
import { AppDOM } from "utils/app.utils";
import {
  CALCULATION_HISTORY_UPDATED_EVENT,
  CalculationHistoryEntry,
  clearCalculationHistory,
  formatHistoryDate,
  readCalculationHistory,
} from "utils/calculationHistory.utils";
import s from "./RecentLoading.module.scss";

export const RecentLoading = ({ buttonClassName }: { buttonClassName?: string }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [history, setHistory] = useState<CalculationHistoryEntry[]>(() => readCalculationHistory());
  const recentWrapperRef = useRef(null);

  useOnClickOutside({
    ref: recentWrapperRef,
    shadowRoot: AppDOM.shadowRootNode,
    handler: () => setIsOpen(false),
  });

  useEffect(() => {
    const refresh = () => setHistory(readCalculationHistory());
    window.addEventListener(CALCULATION_HISTORY_UPDATED_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(CALCULATION_HISTORY_UPDATED_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  const handleSelect = (entry: CalculationHistoryEntry) => {
    dispatch(importGroups(structuredClone(entry.groups)));
    dispatch(setCustomPallets(structuredClone(entry.customPallets)));
    dispatch(setUnitSystem(entry.unitSystem));
    setIsOpen(false);
  };

  const handleClearHistory = () => {
    clearCalculationHistory();
    setHistory([]);
  };

  return (
    <div className={s.recentWrapper} ref={recentWrapperRef}>
      <Button
        variant={BUTTON_VARIANT.TERTIARY}
        className={buttonClassName}
        active={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <ArrowLeftRotate />
        {t("History")}
      </Button>
      <div className={cn(s.dropdown, { [s.open]: isOpen })}>
        <p className={s.title}>{t("Recent loading")}</p>

        {history.length > 0 ? (
          <>
            <ul className={s.list}>
              {history.map((entry) => {
                const groups = entry.summary?.length
                  ? entry.summary
                  : entry.groups.map((group) => ({
                      id: group.id,
                      name: group.name,
                      cargoesCount: group.cargoRows.length,
                    }));

                return (
                  <li className={s.item} key={entry.id}>
                    <button type="button" className={s.itemButton} onClick={() => handleSelect(entry)}>
                      <ArrowRightRotate className={s.icon} />

                      <span className={s.content}>
                        {groups.map(({ id, name, cargoesCount }) => (
                          <span className={s.cargoes} key={id}>
                            {name} {`(${cargoesCount}`} {cargoesCount <= 1 ? "cargo)" : "cargoes)"}
                          </span>
                        ))}

                        <span className={s.date}>{formatHistoryDate(entry.createdAt)}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            <div className={s.footer}>
              <Button variant={BUTTON_VARIANT.TERTIARY} className={s.clearBtn} onClick={handleClearHistory}>
                {t("Clear history")}
              </Button>
            </div>
          </>
        ) : (
          <p className={s.empty}>{t("No recent data available")}</p>
        )}
      </div>
    </div>
  );
};
